import React from 'react';

import { type UseFormReturn, type FieldValues, type FieldPath } from 'react-hook-form';

import { InputWithInlineTitle } from 'src/registry/components/inputs/InputWithInlineTitle';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type InputFormFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label?: string;
    type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
    className?: React.InputHTMLAttributes<HTMLInputElement>['className'];
    disabled?: boolean;
    inlineTitle?: string;
    pattern?: RegExp;
    placeholder?: string;
    required?: boolean;
};

export default function InputFormField<TData extends FieldValues>({
    form,
    name,
    label,
    type,
    className,
    disabled,
    inlineTitle,
    pattern,
    required = true,
    placeholder: placeholderProp,
}: InputFormFieldProps<TData>) {
    const typeIsNumber = type === 'number';

    const placeholder = placeholderProp ?? (label ? `${label}...` : undefined);
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem variant="dialog">
                    {label && (
                        <FormLabel variant="dialog" required={required}>
                            {label}
                        </FormLabel>
                    )}
                    <FormControlStack>
                        <FormControl>
                            {inlineTitle ? (
                                <InputWithInlineTitle
                                    {...field}
                                    inlineTitle={inlineTitle}
                                    className={className}
                                    onChange={
                                        typeIsNumber
                                            ? (e) => {
                                                  const value = Number(e.target.value) || 0;
                                                  field.onChange(value);
                                              }
                                            : (e) => {
                                                  const value = e.target.value;
                                                  if (pattern && !pattern.test(value)) return;
                                                  field.onChange(value);
                                              }
                                    }
                                    value={
                                        typeIsNumber
                                            ? field.value || field.value == 0
                                                ? field.value
                                                : ''
                                            : field.value
                                    }
                                    disabled={disabled}
                                    placeholder={placeholder}
                                />
                            ) : (
                                <Input
                                    {...field}
                                    className={className}
                                    onChange={
                                        typeIsNumber
                                            ? (e) => {
                                                  const value = Number(e.target.value) || 0;
                                                  field.onChange(value);
                                              }
                                            : (e) => {
                                                  const value = e.target.value;
                                                  if (pattern && !pattern.test(value)) return;
                                                  field.onChange(value);
                                              }
                                    }
                                    value={
                                        typeIsNumber
                                            ? field.value || field.value == 0
                                                ? field.value
                                                : ''
                                            : (field.value ?? '')
                                    }
                                    disabled={disabled}
                                    placeholder={placeholder}
                                />
                            )}
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );
}
